// The worked table behind one community's number ("Why 51? See the math") + the band bar.
import { useState } from "react";
import { PulseDrill } from "./PulseDrill";
const MONO = "font-mono tabular-nums";
const EDGE = 0.05; // within 5% of the healthy→trouble range counts as "right on the line"

export function BandBar({ score, thresholds, name }) {
  const th = thresholds || { red_below: 60, green_from: 80 };
  const has = typeof score === "number";
  return (
    <div className="mt-7 relative" data-testid="pulse-band-bar">
      {has && (
        <div className="absolute -top-5 text-[11px] font-bold text-[#1C1917] whitespace-nowrap"
          style={score > 55 ? { right: `calc(${100 - score}% + 6px)` } : { left: `calc(${score}% + 6px)` }}>
          {name ? `${name} · ${score}` : score}
        </div>
      )}
      <div className={`flex h-[26px] rounded-lg overflow-hidden text-white text-[11px] font-bold ${MONO}`}>
        <span className="grid place-items-center bg-[#D32F2F]" style={{ width: `${th.red_below}%` }}>Red &lt; {th.red_below}</span>
        <span className="grid place-items-center bg-[#D97706]" style={{ width: `${th.green_from - th.red_below}%` }}>Yellow</span>
        <span className="grid place-items-center bg-[#16A34A]" style={{ width: `${100 - th.green_from}%` }}>Green</span>
      </div>
      {has && <div className="absolute -top-1.5 -bottom-1.5 w-[3px] bg-[#1C1917] rounded-sm -translate-x-1/2" style={{ left: `${score}%` }} />}
    </div>
  );
}

const unitOf = (x) => (x.unit === "stars" ? "★" : "%");
const onTheLine = (x) => {
  if (x.status !== "ok" || x.value == null || (x.grade || 0) > 0) return false;
  const range = Math.abs(x.trouble - x.healthy) || 1;
  return Math.abs(x.value - x.healthy) <= range * EDGE;
};
const joinNames = (arr) => arr.length <= 1 ? arr.join("") : `${arr.slice(0, -1).join(", ")} and ${arr[arr.length - 1]}`;

function RangeBar({ x }) {
  const g = Math.max(0, Math.min(100, x.grade || 0));
  const tone = g === 0 ? "#16A34A" : g < 50 ? "#D97706" : "#B91C1C";
  const hi = x.better === "high";
  const u = unitOf(x);
  return (
    <div className="min-w-[150px]" data-testid={`pulse-math-bar-${x.key}`}>
      <div className="relative h-[6px] rounded-full bg-gradient-to-r from-[#BBF7D0] via-[#FDE68A] to-[#FECACA]">
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-[13px] w-[13px] rounded-full border-2 border-white shadow-sm" style={{ left: `${g}%`, background: tone }} />
      </div>
      <div className={`flex justify-between text-[10px] text-[#8C827A] mt-1 ${MONO}`}>
        <span>healthy {hi ? "≥" : "≤"}{x.healthy}{u}</span>
        <span>trouble {hi ? "≤" : "≥"}{x.trouble}{u}</span>
      </div>
    </div>
  );
}

function RowNote({ x }) {
  if (x.status === "na") return <span className="ml-1.5 text-[10px] font-semibold text-[#A8A29E]">not used here</span>;
  if (x.status === "insufficient") {
    return <span className="block text-[10px] font-semibold text-[#A8A29E] mt-0.5">{x.key === "unhappy" ? "not enough ratings yet" : x.key === "follow_through" ? "not enough requests yet" : "not enough activity"}{x.weight > 0 ? ` · its ${x.weight} points are shared out` : ""}</span>;
  }
  const g = Math.round(x.grade || 0);
  const edge = onTheLine(x);
  return (
    <span className="block text-[10px] mt-0.5">
      {x.normal != null && <span className="text-[#A8A29E]">your normal {x.display_normal} · </span>}
      {x.extra?.blended && <span className="text-[#A8A29E]" data-testid={`pulse-math-blended-${x.key}`}>{x.n} records, so blended with your {x.extra.prior_from === "normal" ? "normal" : "healthy line"} · </span>}
      {edge ? <span className="font-bold text-[#B45309]" data-testid={`pulse-math-edge-${x.key}`}>right on the healthy line</span>
        : g === 0 ? <span className="font-semibold text-[#15803D]">better than healthy</span>
        : <span className="font-semibold text-[#B91C1C]">{g}% of the way to trouble</span>}
    </span>
  );
}

function Verdict({ rows, score }) {
  const live = rows.filter((x) => x.status === "ok");
  const costing = live.filter((x) => (x.lost || 0) >= 0.5);
  const edge = live.filter(onTheLine);
  const lost = Math.round(live.reduce((a, x) => a + (x.lost || 0), 0));
  return (
    <p className="text-[13px] leading-snug text-[#1C1917] mb-3" data-testid="pulse-math-verdict">
      <span className="font-extrabold">{score}</span>
      {costing.length === 0 ? (
        <> because {live.length === 1 ? "the one measurable signal is" : `all ${live.length} measurable signals are`} at or better than {live.length === 1 ? "its" : "their"} healthy line, so nothing comes off.</>
      ) : (
        <> because {joinNames(costing.map((x) => `${x.label} (${x.display})`))} {costing.length === 1 ? "is" : "are"} past the healthy line — {lost} point{lost === 1 ? "" : "s"} off. {live.length - costing.length > 0 && <>The other {live.length - costing.length === 1 ? "signal is" : `${live.length - costing.length} are`} clear.</>}</>
      )}
      {edge.length > 0 && (
        <span className="text-[#B45309] font-semibold"> {edge.length === 1 ? "One is" : `${edge.length} are`} right on the line — {joinNames(edge.map((x) => `${x.label} (${x.display})`))} — one notch worse and points start coming off.</span>
      )}
    </p>
  );
}

function SharedOut({ rows }) {
  const live = rows.filter((x) => x.status === "ok" && x.weight > 0);
  const missing = rows.filter((x) => x.status !== "ok" && x.weight > 0);
  if (!live.length || !missing.length) return null;
  const pts = missing.reduce((a, x) => a + x.weight, 0);
  return (
    <span data-testid="pulse-math-shared-out">
      {" "}{joinNames(missing.map((x) => `${x.label} (${x.weight})`))} {missing.length === 1 ? "has" : "have"} no usable data this month, so {missing.length === 1 ? "its" : "their"} {pts} points are shared across the other {live.length} — that's why the stakes read {live.map((x) => Math.round(x.weight_used)).join(" · ")} instead of {live.map((x) => x.weight).join(" · ")}.
    </span>
  );
}

export function PulseMath({ math, name, compact = false, tenantId = null }) {
  const [drill, setDrill] = useState(null);
  if (!math) return <div className="text-sm text-[#8C827A]" data-testid="pulse-math-empty">No snapshot yet — the first score lands tomorrow morning.</div>;
  const rows = math.signals || [];
  const lost = Math.round(rows.reduce((a, x) => a + (x.lost || 0), 0));
  const quiet = math.score == null;
  const drillable = (x) => !!tenantId && x.status !== "na";
  return (
    <div className="rounded-2xl border border-[#E8E2D9] bg-[#FAF8F5] px-4 py-3.5" data-testid="pulse-math">
      <div className="flex items-center justify-between text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#8C827A] mb-2">
        <span>The math{name ? ` · ${name}` : ""} · this month</span>
        <span>{quiet ? "Quiet" : `Pulse ${math.score}`}</span>
      </div>
      {!quiet && <Verdict rows={rows} score={math.score} />}
      {tenantId && <p className="text-[11px] text-[#8C827A] mb-1.5" data-testid="pulse-math-drill-hint">Click a signal to see the exact conversations and requests behind it.</p>}
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.06em] text-[#8C827A]">
            <th className="text-left py-1 px-1.5 font-bold">Signal</th>
            <th className="text-right py-1 px-1.5 font-bold">This month</th>
            {!compact && <th className="hidden sm:table-cell text-left py-1 px-3 font-bold">Healthy → trouble</th>}
            {!compact && <th className="hidden sm:table-cell text-right py-1 px-1.5 font-bold whitespace-nowrap">Points at stake</th>}
            <th className="text-right py-1 px-1.5 font-bold whitespace-nowrap">Points off</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => {
            const dim = x.status !== "ok";
            const can = drillable(x);
            return (
              <tr key={x.key} data-testid={`pulse-math-row-${x.key}`} onClick={can ? () => setDrill(x.key) : undefined}
                className={`border-t border-[#E8E2D9] ${dim ? "text-[#A8A29E]" : "text-[#1C1917]"} ${can ? "cursor-pointer hover:bg-white/70 transition-colors group" : ""}`}>
                <td className="py-2 px-1.5 sm:min-w-[190px]">
                  {can ? (
                    <button type="button" data-testid={`pulse-math-drill-${x.key}`} onClick={(e) => { e.stopPropagation(); setDrill(x.key); }}
                      className="sm:whitespace-nowrap font-semibold text-left underline decoration-dotted decoration-[#C9BFB3] underline-offset-[3px] group-hover:decoration-ink">{x.label} <span className="text-[#8C827A] font-normal">›</span></button>
                  ) : <span className="sm:whitespace-nowrap">{x.label}</span>}
                  <RowNote x={x} />
                  {!compact && x.status === "ok" && <div className="block sm:hidden mt-1.5 pr-2"><RangeBar x={x} /><span className="text-[10px] text-[#8C827A]">{Math.round(x.weight_used)} points at stake</span></div>}
                </td>
                <td className={`py-2 px-1.5 text-right align-top ${MONO}`}>{x.status === "na" ? "—" : x.display}</td>
                {!compact && <td className="hidden sm:table-cell py-2 px-3 align-top">{x.status === "ok" ? <RangeBar x={x} /> : <span className="text-[#A8A29E]">—</span>}</td>}
                {!compact && <td className={`hidden sm:table-cell py-2 px-1.5 text-right align-top ${MONO}`}>{x.status === "ok" ? Math.round(x.weight_used) : "—"}</td>}
                <td className={`py-2 px-1.5 text-right align-top font-bold ${MONO} ${x.lost >= 0.5 ? "text-[#B91C1C]" : "text-[#A8A29E]"}`}>{x.status === "ok" ? Math.round(x.lost) : "—"}</td>
              </tr>
            );
          })}
          {!quiet && (
            <>
              <tr className={`border-t-2 border-[#D8CEC1] font-extrabold ${compact ? "" : "sm:hidden"}`}>
                <td colSpan={2} className="py-2 px-1.5">100 at stake − {lost} off</td>
                <td className={`py-2 px-1.5 text-right ${MONO}`} data-testid={compact ? "pulse-math-score" : undefined}>= {math.score}</td>
              </tr>
              {!compact && (
                <tr className="hidden sm:table-row border-t-2 border-[#D8CEC1] font-extrabold">
                  <td colSpan={4} className="py-2 px-1.5">100 at stake − {lost} off</td>
                  <td className={`py-2 px-1.5 text-right ${MONO}`} data-testid="pulse-math-score">= {math.score}</td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
      <div className="text-[11px] text-[#8C827A] mt-2 leading-relaxed">
        How a signal costs points: at or better than its healthy line → 0 off · at or worse than its trouble line → all of its points · in between → a proportional share.
        <SharedOut rows={rows} />
      </div>
      {!quiet && <BandBar score={math.score} thresholds={math.thresholds} name={name} />}
      {tenantId && <PulseDrill tenantId={tenantId} signal={drill} name={name} onClose={() => setDrill(null)} />}
    </div>
  );
}
